/**
 * DeckExport: Export-Funktionen für Decks
 * Unterstützt verschiedene Formate: Text, Cardmarket, MTGA, CSV
 * (Kopieren/Download übernimmt `ui/ExportModal` — diese Datei liefert nur Inhalt/Dateiname)
 *
 * tokenMode ('none' | 'with' | 'only'):
 * - 'none' (Default): nur Deck-Karten, keine Tokens (bisheriges Verhalten).
 * - 'with': Deck-Karten plus eine Token-Sektion.
 * - 'only': ausschließlich Tokens; die Deck-Karten-Optionen (onlyMissing,
 *   excludeBasicLands, excludedCategories) sind in diesem Modus wirkungslos.
 * Token-Zeilen sind schlicht `1 <Name>` (Cardmarket-Bestelllisten-Format);
 * Stärke/Widerstand bleibt außen vor, weil Cardmarket nur den Namen parst.
 */

// ========== EXPORT FORMATE ==========
export const EXPORT_FORMATS = {
  TEXT: 'text',             // universelle flache Liste (Moxfield, Archidekt, Cardmarket ohne Prints)
  CARDMARKET: 'cardmarket', // flache Liste + (Cardmarket-Set-Name) bei explizit gewähltem Print
  MTGA: 'mtga',             // flache Liste + (SET) Nummer bei explizit gewähltem Print
  CSV: 'csv',
};

// ========== TOKEN-HELFER ==========
const includeCards = (tokenMode) => tokenMode !== 'only';
const includeTokens = (tokenMode) => tokenMode === 'with' || tokenMode === 'only';

/** Dedupliziert Tokens nach id (das Aggregat kann pro Karte dieselbe Token-Relation liefern). */
function dedupeTokens(tokens) {
  const seen = new Set();
  const out = [];
  for (const token of tokens || []) {
    if (token && token.id != null && !seen.has(token.id)) {
      seen.add(token.id);
      out.push(token);
    }
  }
  return out;
}

/**
 * Berechnet die Export-Menge einer Karte.
 * Bei onlyMissing: max(0, quantity - ownedQuantity)
 */
function getExportQuantity(card, onlyMissing) {
  const qty = card.quantity || 1;
  if (!onlyMissing) return qty;
  const owned = card.totalOwnedQuantity ?? card.ownedQuantity ?? 0;
  return Math.max(0, qty - owned);
}

/**
 * Prüft ob eine Karte ein Basic Land ist (superType enthält 'Basic' UND type enthält 'Land').
 */
function isBasicLand(card) {
  const superTypes = card.superTypes || [];
  const types = card.types || [];
  const hasBasic = superTypes.some(st => st.toLowerCase() === 'basic');
  const hasLand = types.some(t => t.toLowerCase() === 'land');
  return hasBasic && hasLand;
}

/**
 * Filtert Karten für Export. Entfernt Karten mit Menge 0 bei onlyMissing,
 * optional Karten aus ausgeschlossenen Kategorien und optional Basic Lands.
 */
function filterCardsForExport(cards, { onlyMissing = false, excludedCategories = new Set(), excludeBasicLands = false } = {}) {
  if (!cards) return [];
  return cards.filter(card => {
    if (excludedCategories.has(card.category?.toLowerCase())) return false;
    if (excludeBasicLands && isBasicLand(card)) return false;
    const qty = getExportQuantity(card, onlyMissing);
    return qty > 0;
  });
}

/** Karten in Export-Reihenfolge: Commander zuerst, dann Kategorien alphabetisch. */
function collectOrderedCards(deck, opts) {
  const cards = filterCardsForExport(deck?.cards, opts);
  const commanders = cards.filter(c => c.isCommander);
  const rest = cards.filter(c => !c.isCommander);
  const byCategory = new Map();
  for (const card of rest) {
    const category = card.category || 'Other';
    if (!byCategory.has(category)) byCategory.set(category, []);
    byCategory.get(category).push(card);
  }
  const ordered = [...commanders];
  for (const category of [...byCategory.keys()].sort()) {
    ordered.push(...byCategory.get(category));
  }
  return ordered;
}

/** Eine Textzeile je Karte; lineSuffix liefert formatabhängige Set-Zusätze. */
function exportAsLines(deck, { onlyMissing = false, excludedCategories = new Set(), excludeBasicLands = false, tokenMode = 'none' } = {}, lineSuffix = () => '') {
  const lines = [];
  if (includeCards(tokenMode)) {
    for (const card of collectOrderedCards(deck, { onlyMissing, excludedCategories, excludeBasicLands })) {
      lines.push(`${getExportQuantity(card, onlyMissing)} ${card.name}${lineSuffix(card)}`);
    }
  }
  if (includeTokens(tokenMode)) {
    for (const token of dedupeTokens(deck?.tokens)) {
      lines.push(`1 ${token.name}`);
    }
  }
  return lines.join('\n').trim();
}

/**
 * Exportiert ein Deck als universelle flache Textliste (Moxfield, Archidekt, …)
 */
export function exportAsText(deck, opts = {}) {
  return exportAsLines(deck, opts);
}

/**
 * Exportiert ein Deck als flache Liste + Cardmarket-Set-Namen bei explizit gewähltem Print.
 * Bei vorhandenem `cardmarketRefs`-Eintrag (Ref über die Karten-`id`, nur bei printLocked)
 * geht die versions-genaue Expansion (` (V.N) (Expansion)`) vor der setMap-Fallback-Logik.
 */
export function exportAsCardmarket(deck, opts = {}) {
  const setMap = opts.setMap || {};
  const refs = opts.cardmarketRefs || {};
  return exportAsLines(deck, opts, (card) => {
    if (!card.printLocked) return '';
    const ref = refs[card.id];
    if (ref) {
      const vPart = ref.version != null ? ` (V.${ref.version})` : '';
      return `${vPart} (${ref.expansion})`;
    }
    const cmName = card.set ? setMap[card.set]?.cardmarketName : null;
    return cmName ? ` (${cmName})` : '';
  });
}

/**
 * Exportiert ein Deck als flache Liste + (SET) Sammlernummer bei explizit gewähltem Print.
 */
export function exportAsMTGA(deck, opts = {}) {
  return exportAsLines(deck, opts, (card) => (
    card.printLocked && card.set && card.collectorNumber
      ? ` (${card.set.toUpperCase()}) ${card.collectorNumber}`
      : ''
  ));
}

/**
 * Exportiert ein Deck als CSV
 */
export function exportAsCSV(deck, { onlyMissing = false, excludedCategories = new Set(), excludeBasicLands = false, tokenMode = 'none' } = {}) {
  const headers = ['Quantity', 'Name', 'Category', 'CMC', 'Types', 'Mana Cost', 'Set', 'Collector Number'];
  const rows = [headers.join(',')];

  if (includeCards(tokenMode)) {
    const cards = filterCardsForExport(deck?.cards, { onlyMissing, excludedCategories, excludeBasicLands });
    for (const card of cards) {
      const row = [
        getExportQuantity(card, onlyMissing),
        `"${(card.name || '').replace(/"/g, '""')}"`,
        `"${(card.category || '').replace(/"/g, '""')}"`,
        card.cmc ?? '',
        `"${(card.types || []).join(' ').replace(/"/g, '""')}"`,
        `"${(card.manaCost || '').replace(/"/g, '""')}"`,
        card.set ?? '',
        card.collectorNumber ?? '',
      ];
      rows.push(row.join(','));
    }
  }

  if (includeTokens(tokenMode)) {
    for (const token of dedupeTokens(deck?.tokens)) {
      const row = [
        1,
        `"${(token.name || '').replace(/"/g, '""')}"`,
        '"Token"',
        '',
        '""',
        '""',
        '',
        '',
      ];
      rows.push(row.join(','));
    }
  }

  return rows.join('\n');
}

/**
 * Generiert Export-Content basierend auf Format und Optionen
 * @param {object} opts - u.a. `setMap` (Set-Kürzel → {name, cardmarketName}) und
 *   `cardmarketRefs` ({ [scryfallPrintId]: { expansion, version } | null }) — greift NUR im
 *   Cardmarket-Format und NUR bei `card.printLocked`; hat Vorrang vor `setMap`.
 */
export function generateExportContent(deck, format, opts = {}) {
  switch (format) {
    case EXPORT_FORMATS.CARDMARKET:
      return exportAsCardmarket(deck, opts);
    case EXPORT_FORMATS.MTGA:
      return exportAsMTGA(deck, opts);
    case EXPORT_FORMATS.CSV:
      return exportAsCSV(deck, opts);
    case EXPORT_FORMATS.TEXT:
    default:
      return exportAsText(deck, opts);
  }
}

/** Dateiname: <Deckname>[_missing|_tokens][_cardmarket|_mtga].<txt|csv> */
export function buildExportFilename(deck, format, { onlyMissing = false, tokenMode = 'none' } = {}) {
  const deckName = (deck?.name || 'deck').replace(/[^a-zA-Z0-9-_ äöüÄÖÜß]/gi, '');
  const stateSuffix = tokenMode === 'only' ? '_tokens' : (onlyMissing ? '_missing' : '');
  const formatSuffix = format === EXPORT_FORMATS.CARDMARKET ? '_cardmarket'
    : format === EXPORT_FORMATS.MTGA ? '_mtga' : '';
  const ext = format === EXPORT_FORMATS.CSV ? 'csv' : 'txt';
  return `${deckName}${stateSuffix}${formatSuffix}.${ext}`;
}

const DeckExport = {
  EXPORT_FORMATS,
  exportAsText,
  exportAsCardmarket,
  exportAsMTGA,
  exportAsCSV,
  generateExportContent,
  buildExportFilename,
};

export default DeckExport;
